import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import {
  REMINDER_INTERVALS,
  type ReminderScheduleInterval,
} from '../../domain/reminder-interval.value-object';
import {REMINDER_WEEKDAYS, type ReminderWeekday} from '../../domain/reminder-weekday.value-object';

export type ReminderScheduleFormValues = {
  enabled: boolean;
  interval: ReminderScheduleInterval;
  weekdays: ReminderWeekday[];
};

export class ReminderScheduleValidator implements Validator<ReminderScheduleFormValues> {
  readonly standardSchema = z
    .object({
      enabled: z.boolean(),
      interval: z.enum(REMINDER_INTERVALS),
      weekdays: z
        .array(z.literal(REMINDER_WEEKDAYS))
        .max(REMINDER_WEEKDAYS.length)
        .refine(days => new Set(days).size === days.length, 'Weekdays must be unique.'),
    })
    .refine(
      data => !data.enabled || data.interval !== 'selected_days' || data.weekdays.length > 0,
      {
        message: 'Select at least one day.',
        path: ['weekdays'],
      },
    );

  validate(value: unknown): Result<ReminderScheduleFormValues, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(
        new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid reminder schedule.'),
      );
    }
    return ok(parsed.data);
  }
}
